import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiCheckCircle } from 'react-icons/fi';
import { FaWhatsapp, FaFacebookF, FaInstagram, FaYoutube } from 'react-icons/fa6';
import Breadcrumb from '../components/common/Breadcrumb';
import ErrorMessage from '../components/common/ErrorMessage';
import api from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const ContactPage = () => {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [footerData, setFooterData] = useState(null);

    useEffect(() => {
        const fetchFooter = async () => {
            try {
                const res = await api.get('/footer');
                setFooterData(res);
            } catch (err) {
                console.error('Failed to fetch footer data');
            }
        };
        fetchFooter();
    }, []);

    const contactInfo = footerData?.contactInfo ? [
        { icon: FiPhone, title: 'Phone', details: [footerData.contactInfo.phone || '+92 300 1234567'], action: `tel:${footerData.contactInfo.phone}` },
        { icon: FiMail, title: 'Email', details: [footerData.contactInfo.email || 'support@arshivastra.pk'], action: `mailto:${footerData.contactInfo.email}` },
        { icon: FiMapPin, title: 'Visit Us', details: [(footerData.contactInfo.address || 'Office #123, Main Boulevard').split(',').map(s => s.trim())].flat(), action: 'https://maps.google.com/?q=Lahore' },
        { icon: FiClock, title: 'Working Hours', details: ['Monday - Saturday: 10AM - 8PM', 'Sunday: Closed'], action: null }
    ] : [
        { icon: FiPhone, title: 'Phone', details: ['+92 300 1234567', '+92 42 1234567'], action: 'tel:+923001234567' },
        { icon: FiMail, title: 'Email', details: ['support@arshivastra.pk', 'info@arshivastra.pk'], action: 'mailto:support@arshivastra.pk' },
        { icon: FiMapPin, title: 'Visit Us', details: ['Office #123, Main Boulevard', 'Gulberg, Lahore, Pakistan'], action: 'https://maps.google.com/?q=Lahore' },
        { icon: FiClock, title: 'Working Hours', details: ['Monday - Saturday: 10AM - 8PM', 'Sunday: Closed'], action: null }
    ];

    const socialLinks = footerData?.socialLinks ? footerData.socialLinks.map(s => {
        let icon;
        if (s.icon === 'FaFacebookF') icon = FaFacebookF;
        else if (s.icon === 'FaYoutube') icon = FaYoutube;
        else if (s.icon === 'FaInstagram') icon = FaInstagram;
        else icon = FaWhatsapp; // fallback
        return { icon, label: s.name, link: s.link };
    }) : [];

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        if (!formData.message.trim()) newErrors.message = 'Message is required';
        return newErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setLoading(true);
        setSubmitError(null);
        try {
            await api.post('/contact', formData);
            setSubmitted(true);
            setFormData({ name: '', email: '', phone: '', message: '' });
        } catch (err) {
            setSubmitError(err.response?.data?.message || 'Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white min-h-screen">
            <div className="border-b">
                <div className="container py-4">
                    <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'Contact Us', path: '/contact' }]} />
                </div>
            </div>
            <section
                className="py-16"
                style={{ background: 'var(--primary-color)' }}
            >
                <div className="container-custom text-center text-white">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
                    <p className="text-xl max-w-2xl mx-auto opacity-90">Have questions? We're here to help. Reach out to us anytime.</p>
                </div>
            </section>
            <section className="py-16">
                <div className="container-custom">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {contactInfo.map((info, index) => {
                            const Icon = info.icon;
                            return (
                                <div key={index} className="bg-white p-6 rounded-xl shadow-md text-center border border-gray-100">
                                    <div
                                        className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                                        style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
                                    >
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-semibold text-gray-900 mb-2">{info.title}</h3>
                                    {info.details.map((detail, i) => <p key={i} className="text-gray-600 text-sm">{detail}</p>)}
                                    {info.action && (
                                        <a
                                            href={info.action}
                                            className="text-sm hover:underline mt-2 inline-block"
                                            style={{ color: 'var(--primary-color)' }}
                                            target={info.action.startsWith('http') ? '_blank' : '_self'}
                                            rel="noopener noreferrer"
                                        >
                                            {info.title === 'Phone' ? 'Call Now' : info.title === 'Email' ? 'Send Email' : 'Get Directions'}
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
            <section className="py-16 bg-gray-50">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12">
                        <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
                            {submitError && (
                                <ErrorMessage type="error" message={submitError} onDismiss={() => setSubmitError(null)} />
                            )}
                            {submitted ? (
                                <div className="text-center py-8">
                                    <div
                                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                                        style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
                                    >
                                        <FiCheckCircle className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h3>
                                    <p className="text-gray-600 mb-4">Thank you for contacting us. We'll get back to you soon.</p>
                                    <button
                                        onClick={() => setSubmitted(false)}
                                        className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                                        style={{ backgroundColor: 'var(--primary-color)' }}
                                    >
                                        Send Another Message
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                            style={{ '--tw-ring-color': 'var(--primary-color)' }}
                                        />
                                        {errors.name && <ErrorMessage.Field error={errors.name} />}
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                                style={{ '--tw-ring-color': 'var(--primary-color)' }}
                                            />
                                            {errors.email && <ErrorMessage.Field error={errors.email} />}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                                                style={{ '--tw-ring-color': 'var(--primary-color)' }}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                                        <textarea
                                            name="message"
                                            rows="5"
                                            value={formData.message}
                                            onChange={handleChange}
                                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
                                            style={{ '--tw-ring-color': 'var(--primary-color)' }}
                                        />
                                        {errors.message && <ErrorMessage.Field error={errors.message} />}
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full py-3 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center"
                                        style={{
                                            backgroundColor: 'var(--primary-color)',
                                            '--hover-color': 'var(--hover-primary)'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--hover-primary)'}
                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--primary-color)'}
                                    >
                                        {loading ? (
                                            <>
                                                <LoadingSpinner size="sm" color="white" className="mr-2" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <FiSend className="mr-2" />
                                                Send Message
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Location</h3>
                                <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
                                    <iframe
                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3402.0!2d74.0!3d31.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDAwJzAwLjAiTiA3NMKwMDAnMDAuMCJF!5e0!3m2!1sen!2s!4v1234567890"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        allowFullScreen=""
                                        loading="lazy"
                                        title="Store Location"
                                    ></iframe>
                                </div>
                            </div>
                            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Connect With Us</h3>
                                <div className="flex flex-wrap gap-4">
                                    {socialLinks.map((social, index) => {
                                        const Icon = social.icon;
                                        return (
                                            <a
                                                key={index}
                                                href={social.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`flex items-center px-4 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors`}
                                            >
                                                <Icon className="mr-2" size={20} />
                                                <span className="text-sm font-medium">{social.label}</span>
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick FAQs</h3>
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">How can I track my order?</h4>
                                        <p className="text-sm text-gray-600">You can track your order by visiting our Track Order page and entering your order number and email address.</p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-1">What is your return policy?</h4>
                                        <p className="text-sm text-gray-600">We offer a 7-day return policy on all unworn products in original condition with all packaging.</p>
                                    </div>
                                </div>
                                <Link to="/faq" className="text-sm hover:underline mt-4 inline-block" style={{ color: 'var(--primary-color)' }}>View all FAQs →</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContactPage;